import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useConfirmation } from '@/components/ui/useConfirmation'
import {
  BookOpen,
  FileText,
  Globe,
  Layers,
  Link2,
  Loader2,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  type KnowledgePage,
  type KnowledgePagePreview,
  useCleanKnowledgeContentWithAI,
  useCreateKnowledgePage,
  useDeleteKnowledgePage,
  useKnowledgePages,
  usePreviewImportKnowledgePage,
  useRescrapeKnowledgePage,
  useSiteImport,
  useUpdateKnowledgePage,
} from '@/api/knowledgePages'
import { KnowledgePageEditor } from './KnowledgePageEditor'
import { SiteImportDialog } from './SiteImportDialog'

interface Props {
  companyId: number | undefined
  agentId: number | undefined
}

// The running site import survives a change of tab (the server keeps its state 6 h).
const importKey = (agentId: number) => `callizy-import-site-${agentId}`
const readImportId = (agentId: number | undefined) => {
  if (!agentId) return null
  try {
    return sessionStorage.getItem(importKey(agentId))
  } catch {
    return null
  }
}
const writeImportId = (agentId: number | undefined, id: string | null) => {
  if (!agentId) return
  try {
    if (id) sessionStorage.setItem(importKey(agentId), id)
    else sessionStorage.removeItem(importKey(agentId))
  } catch {
    // Private browsing: the progress just won't come back after a change of tab.
  }
}

type View =
  | { kind: 'list' }
  | { kind: 'preview'; data: KnowledgePagePreview }
  | { kind: 'edit'; page: KnowledgePage }

export function KnowledgeTabContent({ companyId, agentId }: Props) {
  const { demander, dialogue } = useConfirmation()
  const { t } = useTranslation()
  const tkb = (key: string, fallback: string, opts?: Record<string, unknown>) =>
    t(`manager.aiAgents.${key}`, { defaultValue: fallback, ...(opts ?? {}) })

  const [view, setView] = useState<View>({ kind: 'list' })
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [urlInput, setUrlInput] = useState('')

  const { data: pages = [], isLoading } = useKnowledgePages(companyId, agentId)
  const previewImport = usePreviewImportKnowledgePage(companyId, agentId)
  const createPage = useCreateKnowledgePage(companyId, agentId)
  const updatePage = useUpdateKnowledgePage(companyId, agentId)
  const deletePage = useDeleteKnowledgePage(companyId, agentId)
  const rescrapePage = useRescrapeKnowledgePage(companyId, agentId)
  const cleanContent = useCleanKnowledgeContentWithAI(companyId, agentId)

  // === Import of a whole site ===
  const queryClient = useQueryClient()
  const [siteDialogOpen, setSiteDialogOpen] = useState(false)
  const [importId, setImportIdState] = useState<string | null>(() => readImportId(agentId))
  const setImportId = (id: string | null) => {
    setImportIdState(id)
    writeImportId(agentId, id)
  }
  const siteImport = useSiteImport(companyId, agentId, importId)
  const savedCount = siteImport.data?.pages.filter((p) => p.etat === 'enregistree').length ?? 0

  // Each saved page shows up in the list at once.
  useEffect(() => {
    if (savedCount) void queryClient.invalidateQueries({ queryKey: ['knowledge-pages', companyId, agentId] })
  }, [savedCount, queryClient, companyId, agentId])

  // Import no longer known by the server (over 6 h old): forget it.
  useEffect(() => {
    if (siteImport.isError) setImportId(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteImport.isError])

  // Handler partagé preview + edit : reçoit le markdown courant depuis l'éditeur,
  // appelle l'endpoint stateless, retourne le contenu nettoyé. Aucune
  // sauvegarde ici — l'éditeur affiche le résultat et l'utilisateur valide
  // via le flux POST/PATCH classique.
  const handleCleanWithAI = async (currentMarkdown: string) => {
    try {
      const result = await cleanContent.mutateAsync(currentMarkdown)
      return {
        cleaned: result.cleaned_content,
        ratio: result.compression_ratio,
      }
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      toast.error(err?.response?.data?.error || tkb('kbToastCleanFailed', 'Échec du nettoyage IA'))
      return null
    }
  }

  const handleImportUrl = async () => {
    const saisie = urlInput.trim()
    if (!saisie) {
      toast.error(tkb('kbToastUrlRequired', "L'URL est requise"))
      return
    }
    // « workless.ca » suffit : on ajoute le protocole que le client n'a pas tapé.
    const url = /^[a-z][a-z0-9+.-]*:\/\//i.test(saisie) ? saisie : `https://${saisie}`
    // Validation côté client : évite un round-trip réseau pour un check trivial
    // ET garantit que le message d'erreur est traduit (le backend renvoie le sien
    // en français). Le backend re-valide quand même par sécurité.
    if (!/^https?:\/\//i.test(url)) {
      toast.error(tkb('kbToastUrlInvalidProtocol', "L'URL doit commencer par http:// ou https://"))
      return
    }
    try {
      const preview = await previewImport.mutateAsync(url)
      setImportDialogOpen(false)
      setUrlInput('')
      setView({ kind: 'preview', data: preview })
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      toast.error(err?.response?.data?.error || tkb('kbToastImportFailed', "Échec de l'import"))
    }
  }

  const handleCreateManual = () => {
    const data: KnowledgePagePreview = {
      source_url: '',
      title: tkb('kbNewPageTitle', 'Nouvelle page'),
      content: '',
      raw_scraped_text: '',
    }
    setView({ kind: 'preview', data })
  }

  // === Rendu : preview ou edit ===
  if (view.kind === 'preview') {
    return (
      <KnowledgePageEditor
        mode="preview"
        initial={view.data}
        isSaving={createPage.isPending}
        isCleaning={cleanContent.isPending}
        onCancel={() => setView({ kind: 'list' })}
        onSave={async (input) => {
          // En mode 'preview', l'editor passe toujours un KnowledgePageCreateInput
          // complet (incluant raw_scraped_text). Le cast est sûr ici.
          const createInput = input as import('@/api/knowledgePages').KnowledgePageCreateInput
          await createPage.mutateAsync({
            title: createInput.title,
            source_url: createInput.source_url,
            content: createInput.content,
            raw_scraped_text: createInput.raw_scraped_text,
            enabled: createInput.enabled,
          })
          setView({ kind: 'list' })
        }}
        onCleanWithAI={handleCleanWithAI}
      />
    )
  }

  if (view.kind === 'edit') {
    return (
      <>
      {dialogue}
      <KnowledgePageEditor
        mode="edit"
        initial={view.page}
        isSaving={updatePage.isPending}
        isRescraping={rescrapePage.isPending}
        isCleaning={cleanContent.isPending}
        onCancel={() => setView({ kind: 'list' })}
        onSave={async (input) => {
          await updatePage.mutateAsync({
            pageId: view.page.id,
            input: {
              title: input.title,
              source_url: input.source_url,
              content: input.content,
              enabled: input.enabled,
            },
          })
          setView({ kind: 'list' })
        }}
        onDelete={async () => {
          if (!(await demander({ titre: tkb('kbConfirmDeleteEditor', 'Supprimer définitivement cette page ?') }))) return
          await deletePage.mutateAsync(view.page.id)
          setView({ kind: 'list' })
          toast.success(tkb('kbToastPageDeleted', 'Page supprimée'))
        }}
        onRescrape={async () => {
          try {
            const updated = await rescrapePage.mutateAsync(view.page.id)
            setView({ kind: 'edit', page: updated })
            toast.success(tkb('kbToastRescraped', 'Page re-scrapée et mise à jour'))
          } catch (e) {
            const err = e as { response?: { data?: { error?: string } } }
            toast.error(err?.response?.data?.error || tkb('kbToastRescrapeFailed', 'Échec du re-scraping'))
          }
        }}
        onCleanWithAI={handleCleanWithAI}
      />
      </>
    )
  }

  // === Rendu : liste ===
  return (
    <div className="max-w-4xl space-y-6">
      {dialogue}
      {/* Header explicatif */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
          <h2 className="text-base sm:text-lg font-semibold">{tkb('kbTitle', 'Base de connaissances')}</h2>
        </div>
        <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-400">
          {tkb('kbDescription', 'Ajoutez des pages pour que votre agent IA puisse répondre aux questions sur votre entreprise, vos services, vos tarifs.')}
        </p>
      </div>

      {/* Actions principales */}
      {agentId ? (
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            onClick={() => setSiteDialogOpen(true)}
            className="gap-2 flex-1 justify-center bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Layers className="h-4 w-4" />
            {t('manager.aiAgents.siteImport.button')}
          </Button>
          <Button
            type="button"
            onClick={() => setImportDialogOpen(true)}
            variant="outline"
            className="gap-2 flex-1 justify-center"
          >
            <Globe className="h-4 w-4" />
            {tkb('kbImportFromUrl', 'Importer depuis une URL')}
          </Button>
          <Button
            type="button"
            onClick={handleCreateManual}
            variant="outline"
            className="gap-2 flex-1 justify-center"
          >
            <Plus className="h-4 w-4" />
            {tkb('kbCreateManual', 'Créer une page manuellement')}
          </Button>
        </div>
      ) : (
        <Card className="p-4 border-dashed bg-white dark:bg-slate-900 dark:border-slate-700 text-sm text-gray-600 dark:text-slate-400 text-center">
          {tkb('kbSaveAgentFirst', "Enregistrez d'abord l'agent pour pouvoir ajouter des pages de connaissances.")}
        </Card>
      )}

      {/* Site import going on with its window closed: the progress stays in sight */}
      {agentId && importId && siteImport.data && !siteDialogOpen ? (
        <Card className="flex flex-row items-center gap-3 p-3 sm:p-4 bg-blue-50 dark:bg-blue-500/10 border-blue-100 dark:border-blue-500/20">
          {siteImport.data.statut === 'termine' ? (
            <Layers className="h-4 w-4 flex-shrink-0 text-blue-700 dark:text-blue-300" />
          ) : (
            <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-blue-700 dark:text-blue-300" />
          )}
          <p className="flex-1 text-sm text-blue-900 dark:text-blue-100">
            {siteImport.data.statut === 'termine'
              ? t('manager.aiAgents.siteImport.inlineDone', { count: savedCount })
              : t('manager.aiAgents.siteImport.inlineProgress', {
                  done: siteImport.data.pages.filter((p) => ['enregistree', 'deja', 'echec'].includes(p.etat)).length,
                  total: siteImport.data.pages.length,
                })}
          </p>
          {siteImport.data.statut === 'termine' ? (
            <Button type="button" size="sm" variant="ghost" onClick={() => setImportId(null)}>
              {t('manager.aiAgents.siteImport.ok')}
            </Button>
          ) : (
            <Button type="button" size="sm" variant="outline" onClick={() => setSiteDialogOpen(true)}>
              {t('manager.aiAgents.siteImport.details')}
            </Button>
          )}
        </Card>
      ) : null}

      {/* Liste des pages */}
      {agentId && (
        <div className="space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : pages.length === 0 ? (
            <div className="text-center py-12 px-4 bg-gray-50 dark:bg-slate-800/50 rounded-lg border border-dashed dark:border-slate-700">
              <BookOpen className="h-10 w-10 text-gray-400 mx-auto mb-3" />
              <p className="text-sm text-gray-600 dark:text-slate-400">
                {tkb('kbNoPages', 'Aucune page de connaissances pour le moment.')}
              </p>
              <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                {tkb('kbNoPagesHint', 'Importez depuis une URL ou créez une page manuellement pour commencer.')}
              </p>
            </div>
          ) : (
            <>
              <div className="text-xs text-gray-500 dark:text-slate-400 px-1">
                {(() => {
                  const active = pages.filter((p) => p.enabled).length
                  const key = active > 1 ? 'kbActiveCountPlural' : 'kbActiveCount'
                  const fallback = active > 1
                    ? `${active} actives sur ${pages.length}`
                    : `${active} active sur ${pages.length}`
                  return tkb(key, fallback, { count: active, total: pages.length })
                })()}
              </div>
              {pages.map((page) => (
                <PageCard
                  key={page.id}
                  page={page}
                  onEdit={() => setView({ kind: 'edit', page })}
                  onToggle={async (enabled) => {
                    try {
                      await updatePage.mutateAsync({
                        pageId: page.id,
                        input: { enabled },
                      })
                    } catch {
                      toast.error(tkb('kbToastToggleFailed', 'Impossible de modifier le statut'))
                    }
                  }}
                  onDelete={async () => {
                    if (!(await demander({ titre: tkb('kbConfirmDelete', `Supprimer « ${page.title} » ?`, { title: page.title }) }))) return
                    try {
                      await deletePage.mutateAsync(page.id)
                      toast.success(tkb('kbToastPageDeleted', 'Page supprimée'))
                    } catch {
                      toast.error(tkb('kbToastDeleteFailed', 'Échec de la suppression'))
                    }
                  }}
                />
              ))}
            </>
          )}
        </div>
      )}

      <SiteImportDialog
        open={siteDialogOpen}
        onOpenChange={setSiteDialogOpen}
        companyId={companyId}
        agentId={agentId}
        existingUrls={pages.map((p) => p.source_url)}
        importId={importId}
        importState={siteImport.data}
        onStarted={setImportId}
        onDone={() => setImportId(null)}
      />

      {/* Modal d'import URL */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{tkb('kbImportDialogTitle', 'Importer une page web')}</DialogTitle>
            <DialogDescription>
              {tkb('kbImportDialogDesc', "Collez l'URL d'une page de votre site. On extrait automatiquement le contenu structuré, vous pourrez ensuite l'éditer avant de sauvegarder.")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="kp-url">URL</Label>
            <Input
              id="kp-url"
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !previewImport.isPending) {
                  e.preventDefault()
                  handleImportUrl()
                }
              }}
              placeholder="https://www.exemple.com/page"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setImportDialogOpen(false)}
              disabled={previewImport.isPending}
            >
              {tkb('kbImportDialogCancel', 'Annuler')}
            </Button>
            <Button
              type="button"
              onClick={handleImportUrl}
              disabled={previewImport.isPending || !urlInput.trim()}
              className="gap-2"
            >
              {previewImport.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {tkb('kbImportDialogImport', 'Importer')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// =============================================================================
// Sous-composant : carte d'une page
// =============================================================================

function PageCard({
  page,
  onEdit,
  onToggle,
  onDelete,
}: {
  page: KnowledgePage
  onEdit: () => void
  onToggle: (enabled: boolean) => Promise<void>
  onDelete: () => Promise<void>
}) {
  const { t, i18n } = useTranslation()
  const tkb = (key: string, fallback: string, opts?: Record<string, unknown>) =>
    t(`manager.aiAgents.${key}`, { defaultValue: fallback, ...(opts ?? {}) })
  const isUrl = !!page.source_url
  const dateStr = formatDate(isUrl ? (page.scraped_at || page.created_at) : page.created_at, i18n.language)
  return (
    <Card
      className={`p-4 transition-colors bg-white dark:bg-slate-900 border dark:border-slate-800 text-gray-900 dark:text-slate-100 ${
        page.enabled ? '' : 'opacity-60'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="bg-gray-100 dark:bg-slate-800 p-2 rounded">
          {isUrl ? (
            <Link2 className="h-4 w-4 text-gray-500 dark:text-slate-400" />
          ) : (
            <FileText className="h-4 w-4 text-gray-500 dark:text-slate-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold truncate text-gray-900 dark:text-slate-100">
              {page.title}
            </h3>
            {!page.enabled && (
              <Badge variant="secondary" className="text-xs">
                {tkb('kbCardDisabled', 'Désactivée')}
              </Badge>
            )}
          </div>
          {isUrl && (
            <p className="text-xs text-gray-500 dark:text-slate-400 truncate mt-0.5">
              {page.source_url}
            </p>
          )}
          <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
            {isUrl
              ? tkb('kbCardImportedOn', `Importée le ${dateStr}`, { date: dateStr })
              : tkb('kbCardCreatedOn', `Créée le ${dateStr}`, { date: dateStr })}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Switch
            checked={page.enabled}
            onCheckedChange={onToggle}
            aria-label={tkb('kbCardToggleAria', 'Activer/désactiver cette page')}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onEdit}
            className="h-9 w-9 p-0 dark:hover:bg-slate-800"
            aria-label={tkb('kbCardEdit', 'Modifier')}
          >
            <Pencil className="h-4 w-4 text-gray-500 dark:text-slate-400" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
            aria-label={tkb('kbCardDelete', 'Supprimer')}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  )
}

function formatDate(iso: string, lang = 'fr'): string {
  try {
    const locale = lang.startsWith('en') ? 'en-CA' : 'fr-CA'
    return new Date(iso).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}
